import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

import en from "./locales/en.json";
import {
  getCachedLocale,
  setCachedLocale,
  evictStaleLocales,
} from "./localeCache";

declare const __APP_VERSION__: string;

const APP_VERSION = __APP_VERSION__;

const localeLoaders: Record<string, () => Promise<{ default: unknown }>> = {
  es: () => import("./locales/es.json"),
  fr: () => import("./locales/fr.json"),
  de: () => import("./locales/de.json"),
  pt: () => import("./locales/pt.json"),
  it: () => import("./locales/it.json"),
  nl: () => import("./locales/nl.json"),
  pl: () => import("./locales/pl.json"),
  ru: () => import("./locales/ru.json"),
  tr: () => import("./locales/tr.json"),
  ar: () => import("./locales/ar.json"),
  hi: () => import("./locales/hi.json"),
  zh: () => import("./locales/zh.json"),
  ja: () => import("./locales/ja.json"),
  ko: () => import("./locales/ko.json"),
  vi: () => import("./locales/vi.json"),
  th: () => import("./locales/th.json"),
  id: () => import("./locales/id.json"),
  sw: () => import("./locales/sw.json"),
  tl: () => import("./locales/tl.json"),
  sv: () => import("./locales/sv.json"),
  ro: () => import("./locales/ro.json"),
  el: () => import("./locales/el.json"),
};

const loadedLanguages = new Set<string>(["en"]);
const inflightLoads = new Map<string, Promise<void>>();

function normalizeLang(lang: string | undefined | null): string {
  if (!lang) return "en";
  const base = lang.toLowerCase().split(/[-_]/)[0];
  if (base === "en" || localeLoaders[base]) return base;
  return "en";
}

async function loadLanguage(lang: string): Promise<void> {
  const code = normalizeLang(lang);
  if (loadedLanguages.has(code)) return;
  const loader = localeLoaders[code];
  if (!loader) return;

  let pending = inflightLoads.get(code);
  if (!pending) {
    pending = (async () => {
      const isDev = APP_VERSION === "dev";

      // Try the IndexedDB cache first (skips network on repeat visits).
      // Skip in dev so locale file edits are always reflected on reload.
      if (!isDev) {
        const cached = await getCachedLocale(code, APP_VERSION);
        if (cached) {
          i18n.addResourceBundle(code, "translation", cached, true, true);
          loadedLanguages.add(code);
          return;
        }
      }

      // Cache miss (or dev mode) — load from the Vite chunk and then persist.
      // Errors are intentionally propagated so callers (e.g. handleLanguageChange
      // in profile.tsx) can show an error toast and reset loading state.
      const mod = await loader();
      const data = mod.default as Record<string, unknown>;
      i18n.addResourceBundle(code, "translation", data, true, true);
      loadedLanguages.add(code);
      // Write to IndexedDB cache in the background; don't block the UI.
      if (!isDev) void setCachedLocale(code, APP_VERSION, data);
    })().finally(() => {
      inflightLoads.delete(code);
    });
    inflightLoads.set(code, pending);
  }
  return pending;
}

const initPromise = i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
    },
    fallbackLng: "en",
    supportedLngs: ["en", ...Object.keys(localeLoaders)],
    nonExplicitSupportedLngs: true,
    load: "languageOnly",
    partialBundledLanguages: true,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ["localStorage", "navigator"],
      caches: ["localStorage"],
      lookupLocalStorage: "plateOS_language",
    },
  });

export const i18nReady: Promise<void> = initPromise.then(async () => {
  const detected = normalizeLang(i18n.language);
  document.documentElement.lang = detected;
  document.documentElement.dir = detected === "ar" ? "rtl" : "ltr";
  if (detected !== "en") {
    await loadLanguage(detected);
    await i18n.changeLanguage(detected);
  }
  // Evict stale locale entries from previous builds in the background.
  void evictStaleLocales(APP_VERSION);
});

export default i18n;

export async function changeLanguage(lang: string) {
  const code = normalizeLang(lang);
  await loadLanguage(code);
  await i18n.changeLanguage(code);
  localStorage.setItem("plateOS_language", code);
  const dir = code === "ar" ? "rtl" : "ltr";
  document.documentElement.dir = dir;
  document.documentElement.lang = code;
}
